/**
 * pwa.ts — utilitaires d'installation et de notifications du tableau de bord.
 *
 * L'app est un site classique (SPA Vite) ; ce module enregistre le service
 * worker (voir public/sw.js) et expose l'installation + les notifications.
 * Le chemin du service worker suit le base path de l'app.
 */

export function isPwaSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let reloadedForNewController = false;

// ── Détection de mise à jour (nouvelle version publiée) ──
type UpdateHandler = (reg: ServiceWorkerRegistration) => void;
let updateHandlers: UpdateHandler[] = [];

/** S'abonne à la disponibilité d'une mise à jour. Renvoie la fonction de désabonnement. */
export function onUpdateAvailable(handler: UpdateHandler): () => void {
  updateHandlers.push(handler);
  return () => {
    updateHandlers = updateHandlers.filter((h) => h !== handler);
  };
}

function notifyUpdateAvailable(reg: ServiceWorkerRegistration): void {
  updateHandlers.forEach((handler) => {
    try { handler(reg); } catch { /* un abonné défaillant ne bloque pas les autres */ }
  });
}

/**
 * Applique la mise à jour en attente : demande au nouveau service worker de
 * prendre le contrôle immédiatement (SKIP_WAITING) → la page se recharge une
 * fois sur la nouvelle version. Sur iPhone, où le service worker peut ne pas
 * être disponible, on recharge simplement la page.
 */
export async function applyPwaUpdate(): Promise<void> {
  try {
    if (isPwaSupported()) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Le rechargement est déclenché par controllerchange (ensureOneTimeReloadOnNewController).
        return;
      }
      if (reg) { try { await reg.update(); } catch { /* ignore */ } }
    }
  } catch {
    /* on retombe sur le rechargement simple ci-dessous */
  }
  hardReload(300);
}

/**
 * Recharge la page une seule fois lorsqu'un nouveau service worker prend le
 * contrôle. Cela permet de remplacer immédiatement une ancienne version
 * défaillante du service worker par la version corrigée, sans boucle de
 * rechargement.
 */
function ensureOneTimeReloadOnNewController(): void {
  if (!isPwaSupported()) return;
  if (navigator.serviceWorker.controller == null) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadedForNewController) return;
    reloadedForNewController = true;
    window.location.reload();
  });
}

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPwaSupported()) return Promise.resolve(null);
  if (registrationPromise) return registrationPromise;

  const base = import.meta.env.BASE_URL || '/';
  const swUrl = `${base}sw.js`;

  registrationPromise = navigator.serviceWorker
    .register(swUrl, { scope: base })
    .then((reg) => {
      // Une mise à jour peut déjà être « en attente » (détectée auparavant).
      if (reg.waiting && navigator.serviceWorker.controller) {
        notifyUpdateAvailable(reg);
      }
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          // Nouvelle version installée alors qu'une version contrôle déjà la
          // page : il s'agit d'une MISE À JOUR. On prévient l'app (bannière
          // « Mettre à jour l'application ») au lieu d'activer en force.
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            notifyUpdateAvailable(reg);
          }
        });
      });
      // Vérifie activement une mise à jour du service worker au démarrage
      reg.update().catch(() => {});
      return reg;
    })
    .catch(() => null)
    .then((reg) => {
      ensureOneTimeReloadOnNewController();
      return reg;
    });

  return registrationPromise;
}

export function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isPwaSupported()) return Promise.resolve(null);
  return navigator.serviceWorker.ready.catch(() => null);
}

export interface CacheRepairResult {
  ok: boolean;
  clearedCaches: number;
  updated: boolean;
  message: string;
}

/**
 * « Réparer / Vider le cache » : supprime tous les caches de l'application,
 * demande au service worker de les vider, force la vérification d'une mise à
 * jour puis active immédiatement la nouvelle version si elle est en attente.
 * Idéal quand un utilisateur voit une ancienne version figée du site.
 */
export async function repairAppCache(): Promise<CacheRepairResult> {
  let clearedCaches = 0;
  let updated = false;

  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      const results = await Promise.all(
        keys.map((k) => caches.delete(k).then((done) => (done ? 1 : 0)).catch(() => 0)),
      );
      clearedCaches = results.reduce((sum, n) => sum + n, 0);
    }
  } catch {
    /* CacheStorage indisponible : on continue quand même */
  }

  if (isPwaSupported()) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        try { reg.active?.postMessage({ type: 'CLEAR_CACHE' }); } catch { /* ignore */ }
        try { reg.waiting?.postMessage({ type: 'SKIP_WAITING' }); } catch { /* ignore */ }
        try { await reg.update(); updated = true; } catch { /* ignore */ }
        // Un nouveau SW en attente doit prendre le contrôle de la page.
        if (reg.waiting) {
          try { reg.waiting.postMessage({ type: 'SKIP_WAITING' }); } catch { /* ignore */ }
        }
      }
    } catch {
      /* pas de service worker : simple vider de cache suffit */
    }
  }

  return {
    ok: true,
    clearedCaches,
    updated,
    message: 'Cache vidé et mise à jour forcée. Rechargement…',
  };
}

export type NotifPermission = 'default' | 'granted' | 'denied' | 'unsupported';

export function notificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function notificationPermission(): NotifPermission {
  if (!notificationsSupported()) return 'unsupported';
  return Notification.permission as NotifPermission;
}

export async function requestNotificationPermission(): Promise<NotifPermission> {
  if (!notificationsSupported()) return 'unsupported';
  try {
    return (await Notification.requestPermission()) as NotifPermission;
  } catch {
    return 'denied';
  }
}

/**
 * Affiche une notification. Passe par le service worker si disponible
 * (pour un rendu système complet), sinon utilise l'API Notification directe.
 */
export async function showLocalNotification(
  title: string,
  body: string,
  opts: { tag?: string; url?: string } = {},
): Promise<void> {
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  const base = import.meta.env.BASE_URL || '/';
  const url = opts.url || base;

  try {
    const reg = await getServiceWorkerRegistration();
    if (reg) {
      reg.active?.postMessage({
        type: 'SHOW_NOTIFICATION',
        title,
        body,
        tag: opts.tag,
        url,
      });
      return;
    }
  } catch {
    /* fallback ci-dessous */
  }

  try {
    const n = new Notification(title, { body, tag: opts.tag });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    /* notifications indisponibles */
  }
}

/** Recharge la page après un court délai (le temps de propager l'état UI). */
export function hardReload(delayMs = 700): void {
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    try {
      window.location.reload();
    } catch {
      /* ignore */
    }
  }, delayMs);
}